import useCurrentInstance from '../helpers/useCurrentInstance';
import {
    INSTAGRAM_ID,
    FACEBOOK_ID,
    TIKTOK_ID,
    PINTEREST_ID,
    NETWORK_CONNECTION_TYPE_OAUTH,
    NETWORK_CONNECTION_TYPE_SOFT,
    NETWORK_CONNECTION_TYPE_FAILED,
    NETWORK_CONNECTION_PERMISSIONS_FULL,
    NETWORK_ACCOUNT_TYPE_CREATOR,
    META_UPLOADED_CONTENT_DELIVERY_NETWORKS,
    META_UPLOADED_CONTENT_DELIVERY_NETWORK_SLUGS
} from '../../app-v2/constants/networks';
import { CAMPAIGN_TASK_MEDIA_TYPE_STORY } from '../../app-v2/constants/campaigns';
import { useCreatorStore, useFeatureFlagsStore } from '../../stores';

const useNetworks = () => {
    const flags = useFeatureFlagsStore().data;

    const getContactNetworkById = (id) => {
        return useCurrentInstance()?.$root?.networks?.find((network) => network.network?.id === id);
    };

    const getContactNetworkByIdFromContactNetworks = (id, contactNetworks) => {
        if (!id || !contactNetworks) {
            return null;
        }

        return contactNetworks.find((contactNetwork) => contactNetwork.network_id === id);
    };

    const getConnectedContactNetworkByIdFromContactNetworks = (id, contactNetworks) => {
        if (!id || !contactNetworks) {
            return null;
        }

        return contactNetworks.find((contactNetwork) => {
            if (contactNetwork.network_id !== id) {
                return false;
            }

            // 3rd Party - Meta Only - Connected = Soft or OAuth with Full Permissions
            if (flags['third-party-meta-live-site-authentication'] && isMetaById(id)) {
                return isSoftAuthed(contactNetwork) || isOAuthed(contactNetwork);
            }

            // 1st Party - Or any other network - Connected = OAuth with Full Permissions
            return isOAuthed(contactNetwork);
        });
    };

    const isConnectedByIdFromContactNetworks = (id, contactNetworks) => {
        return !!getConnectedContactNetworkByIdFromContactNetworks(id, contactNetworks);
    };

    const isSoftAuthed = (contactNetwork) => {
        return contactNetwork?.network_connection_type === NETWORK_CONNECTION_TYPE_SOFT;
    };

    const isOAuthed = (contactNetwork) => {
        return (
            contactNetwork?.network_connection_type === NETWORK_CONNECTION_TYPE_OAUTH &&
            contactNetwork?.network_connection_permissions === NETWORK_CONNECTION_PERMISSIONS_FULL
        );
    };

    const isFailed = (contactNetwork) => {
        return contactNetwork?.network_connection_type === NETWORK_CONNECTION_TYPE_FAILED;
    };

    const isCreatorAccount = (contactNetwork) => {
        return contactNetwork?.network_account_type === NETWORK_ACCOUNT_TYPE_CREATOR;
    };

    const isMetaById = (id) => {
        return [INSTAGRAM_ID, FACEBOOK_ID].includes(id);
    };

    const isTiktokById = (id) => {
        return TIKTOK_ID === id;
    };

    const isPinterestById = (id) => {
        return PINTEREST_ID === id;
    };

    const isInstagramById = (id) => {
        return INSTAGRAM_ID === id;
    };

    // Activations
    const isMetaUploadContentDeliveryNetworkByType = (type) => {
        return META_UPLOADED_CONTENT_DELIVERY_NETWORKS.includes(type);
    };

    // Campaigns + Partnerships
    const isMetaUploadContentDeliveryNetworkBySlugAndMediaType = (slug, mediaType) => {
        return (
            META_UPLOADED_CONTENT_DELIVERY_NETWORK_SLUGS.includes(slug) && mediaType !== CAMPAIGN_TASK_MEDIA_TYPE_STORY
        );
    };

    const isBrandNetworkConnectedForNetworkById = (brandNetworks, id) => {
        if (!brandNetworks) {
            return false;
        }

        return !!brandNetworks?.find((network) => network.network_id === id && network.has_valid_access_token);
    };

    const isGraphAPIAvailable = (contactNetwork) => {
        return isMetaById(contactNetwork?.network_id) && isOAuthed(contactNetwork);
    };

    const isDiscoveryAPIAvailable = (contactNetwork, brandNetworks) => {
        return (
            isMetaById(contactNetwork?.network_id) &&
            contactNetwork?.network_username &&
            isCreatorAccount(contactNetwork) &&
            isBrandNetworkConnectedForNetworkById(brandNetworks, contactNetwork?.network_id)
        );
    };

    const canPullContentAutomatically = (networkId) => {
        const contactNetwork = getContactNetworkById(networkId);
        if (isTiktokById(networkId) || isPinterestById(networkId)) {
            return contactNetwork && isOAuthed(contactNetwork);
        } else if (isInstagramById(networkId)) {
            return contactNetwork && !isFailed(contactNetwork);
        } else {
            return !!contactNetwork;
        }
    };

    const showUploadedContentDeliveryForMeta = (
        contactNetwork,
        brandNetworks,
        type = null,
        slug = null,
        mediaType = null
    ) => {
        const isMetaUploadContentDelivery = type
            ? isMetaUploadContentDeliveryNetworkByType(type)
            : isMetaUploadContentDeliveryNetworkBySlugAndMediaType(slug, mediaType);

        if (flags['third-party-meta-live-site-authentication']) {
            return (
                isMetaUploadContentDelivery &&
                !isOAuthed(contactNetwork) &&
                !isSoftAuthed(contactNetwork)
            );
        }

        return (
            isMetaUploadContentDelivery &&
            !isDiscoveryAPIAvailable(contactNetwork, brandNetworks) &&
            !isGraphAPIAvailable(contactNetwork)
        );
    };

    const showUploadedContentDeliveryForTiktok = (networkId, contactNetwork) => {
        return isTiktokById(networkId) && !isOAuthed(contactNetwork);
    };

    const showUploadedContentDeliveryForPinterest = (networkId, contactNetwork) => {
        return isPinterestById(networkId) && !isOAuthed(contactNetwork);
    };

    const showUploadedContentDeliveryForNetwork = (
        networkId,
        contactNetwork,
        brandNetworks = null,
        type = null,
        slug = null,
        mediaType = null
    ) => {
        return (
            showUploadedContentDeliveryForMeta(contactNetwork, brandNetworks, type, slug, mediaType) ||
            showUploadedContentDeliveryForTiktok(networkId, contactNetwork) ||
            showUploadedContentDeliveryForPinterest(networkId, contactNetwork)
        );
    };

    const showSharedNetworkPreConnect = (socialNetwork, isLandingPage = false) => {
        return (
            socialNetwork &&
            !isLandingPage &&
            useCreatorStore().worksWithMulitpleBrands &&
            !useCreatorStore().getCurrentLiveUrlSocialNetwork(socialNetwork?.network_id) &&
            flags['live-site-shared-network-connections']
        );
    };

    const showSharedNetworkPostConnect = (socialNetwork, contactNetworks, isLandingPage = false) => {
        const liveUrlsNotConnected = useCreatorStore().getLiveUrlsMissingSocialNetwork(
            socialNetwork?.network_id,
            socialNetwork?.external_id
        );

        const contactNetwork = getContactNetworkByIdFromContactNetworks(socialNetwork?.network_id, contactNetworks);

        return (
            contactNetwork &&
            contactNetwork.network_connection_type === NETWORK_CONNECTION_TYPE_OAUTH &&
            socialNetwork &&
            !isLandingPage &&
            liveUrlsNotConnected?.length > 0 &&
            flags['live-site-shared-network-connections']
        );
    };

    const isInstagramGraphRequiredForActivationBrief = (brief) => {
        return (
            (brief && brief.tasks.filter((task) => task.network_id === INSTAGRAM_ID && task.requires_oauth))?.length > 0
        );
    };

    return {
        getContactNetworkById,
        getContactNetworkByIdFromContactNetworks,
        getConnectedContactNetworkByIdFromContactNetworks,
        isConnectedByIdFromContactNetworks,
        isSoftAuthed,
        isOAuthed,
        isCreatorAccount,
        isMetaById,
        isTiktokById,
        isInstagramById,
        isPinterestById,
        isMetaUploadContentDeliveryNetworkByType,
        isMetaUploadContentDeliveryNetworkBySlugAndMediaType,
        isBrandNetworkConnectedForNetworkById,
        isGraphAPIAvailable,
        isDiscoveryAPIAvailable,
        isInstagramGraphRequiredForActivationBrief,
        canPullContentAutomatically,
        showUploadedContentDeliveryForMeta,
        showUploadedContentDeliveryForTiktok,
        showUploadedContentDeliveryForPinterest,
        showUploadedContentDeliveryForNetwork,
        showSharedNetworkPreConnect,
        showSharedNetworkPostConnect
    };
};

export default useNetworks;
